<c-container fluid>
  @if (error()) {
    <div class="alert alert-danger py-2 d-flex justify-content-between align-items-center gap-2">
      <span>{{ error() }}</span>
      <button type="button" class="btn-close" [attr.aria-label]="'sales_toolkit.actions.cancel' | translate" (click)="error.set(null)"></button>
    </div>
  }

  <c-card class="mb-4">
    <c-card-header>
      <strong>{{ 'sales_toolkit.title' | translate }}</strong>
      <div class="small text-body-secondary">{{ 'sales_toolkit.intro' | translate }}</div>
    </c-card-header>

    <c-card-body>
      <div class="toolbar d-flex flex-wrap align-items-center gap-2 mb-3">
        <button cButton color="primary" type="button" [disabled]="busy()" (click)="startNewFolder()">
          {{ 'sales_toolkit.actions.new_folder' | translate }}
        </button>

        <label class="btn btn-outline-primary mb-0" [class.disabled]="busy()">
          {{ 'sales_toolkit.actions.upload_files' | translate }}
          <input type="file" class="d-none" multiple [accept]="accept" (change)="onFilesPicked($event)" />
        </label>

        <!-- webkitdirectory: the browser picks a whole folder and gives every file
             its path inside it (webkitRelativePath), which becomes relative_path. -->
        <label class="btn btn-outline-primary mb-0" [class.disabled]="busy()">
          {{ 'sales_toolkit.actions.upload_folder' | translate }}
          <input type="file" class="d-none" multiple webkitdirectory (change)="onFolderPicked($event)" />
        </label>

        <c-form-check [switch]="true" class="mb-0 ms-1">
          <input cFormCheckInput id="salesToolkitOverwrite" type="checkbox"
                 [ngModel]="overwrite()" (ngModelChange)="overwrite.set($event)" />
          <label cFormCheckLabel for="salesToolkitOverwrite">{{ 'sales_toolkit.actions.replace_existing' | translate }}</label>
        </c-form-check>

        <button cButton color="secondary" variant="outline" type="button" class="ms-auto" (click)="refresh()">
          {{ 'sales_toolkit.actions.refresh' | translate }}
        </button>
      </div>

      @if (creatingFolder()) {
        <form class="new-folder d-flex flex-wrap align-items-center gap-2 mb-3" (ngSubmit)="createFolder()">
          <input cFormControl type="text" name="newFolderName" maxlength="120" autocomplete="off"
                 class="new-folder-input"
                 [placeholder]="'sales_toolkit.prompts.folder_name' | translate"
                 [ngModel]="newFolderName()" (ngModelChange)="newFolderName.set($event)" />
          <button cButton color="primary" type="submit" [disabled]="busy()">{{ 'sales_toolkit.actions.save' | translate }}</button>
          <button cButton color="secondary" variant="ghost" type="button" (click)="cancelNewFolder()">{{ 'sales_toolkit.actions.cancel' | translate }}</button>
        </form>
      }

      @if (uploads().length || skipped()) {
        <div class="upload-panel mb-3">
          <div class="upload-panel-header d-flex justify-content-between align-items-center gap-2">
            <strong>
              {{ 'sales_toolkit.upload.queue_title' | translate }}
              <span class="text-body-secondary fw-normal">({{ doneCount() }}/{{ uploads().length }})</span>
            </strong>
            <button cButton color="secondary" variant="ghost" size="sm" type="button"
                    [disabled]="uploading()" (click)="clearUploads()">{{ 'sales_toolkit.actions.close' | translate }}</button>
          </div>
          @if (skipped()) {
            <div class="small text-body-secondary px-2 pb-1">{{ 'sales_toolkit.upload.skipped_summary' | translate: { count: skipped() } }}</div>
          }
          <ul class="upload-list">
            @for (upload of uploads(); track upload.id) {
              <li class="upload-row">
                <span class="upload-name" [title]="upload.label">{{ upload.label }}</span>
                @if (upload.state === 'uploading') {
                  <div class="progress upload-progress" role="progressbar" [attr.aria-valuenow]="upload.progress" aria-valuemin="0" aria-valuemax="100">
                    <div class="progress-bar" [style.width.%]="upload.progress"></div>
                  </div>
                } @else {
                  <span class="upload-state small" [ngClass]="stateClass(upload.state)">
                    {{ 'sales_toolkit.upload.states.' + upload.state | translate: stateParams(upload) }}
                    @if (upload.reason) {
                      – {{ upload.reason | translate }}
                    }
                  </span>
                }
              </li>
            }
          </ul>
        </div>
      }

      <!-- The whole listing is the drop target: a folder dropped anywhere on it
           lands in the folder the explorer currently shows. -->
      <div class="drop-zone"
           [class.drag-active]="dragActive()"
           (dragenter)="onDragEnter($event)"
           (dragover)="onDragOver($event)"
           (dragleave)="onDragLeave($event)"
           (drop)="onDrop($event)">
        <media-explorer [media]="media"
                        [source]="source"
                        [exitable]="false">
          <ng-template #entryActions let-entry>
            @if (renaming() === entry.path) {
              <form class="rename-form" (ngSubmit)="saveRename(entry)">
                <input cFormControl sizing="sm" type="text" name="renameValue" maxlength="120" autocomplete="off"
                       [ngModel]="renameValue()" (ngModelChange)="renameValue.set($event)"
                       (keydown.escape)="cancelRename()" />
                <div class="d-flex justify-content-center gap-1 mt-1">
                  <button cButton color="primary" size="sm" type="submit" [disabled]="busy()">{{ 'sales_toolkit.actions.save' | translate }}</button>
                  <button cButton color="secondary" variant="ghost" size="sm" type="button" (click)="cancelRename()">{{ 'sales_toolkit.actions.cancel' | translate }}</button>
                </div>
              </form>
            } @else {
              <button type="button" class="entry-action" [disabled]="busy()"
                      [title]="'sales_toolkit.actions.rename' | translate"
                      [attr.aria-label]="('sales_toolkit.actions.rename' | translate) + ': ' + entry.name"
                      (click)="startRename(entry)">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 17.25V21h3.75L17.8 9.94l-3.75-3.75zm17.7-10.2a1 1 0 0 0 0-1.42l-2.33-2.33a1 1 0 0 0-1.42 0l-1.83 1.83 3.75 3.75z"/></svg>
              </button>
              <button type="button" class="entry-action danger" [disabled]="busy()"
                      [title]="'sales_toolkit.actions.delete' | translate"
                      [attr.aria-label]="('sales_toolkit.actions.delete' | translate) + ': ' + entry.name"
                      (click)="remove(entry)">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6zM19 4h-3.5l-1-1h-5l-1 1H5v2h14z"/></svg>
              </button>
            }
          </ng-template>
        </media-explorer>
        @if (dragActive()) {
          <div class="drop-hint" aria-hidden="true">{{ 'sales_toolkit.upload.drop_hint' | translate }}</div>
        }
      </div>
    </c-card-body>
  </c-card>
</c-container>
